import { injectable } from 'inversify';
import { compact } from 'lodash-es';
import {
    AccreditationsSegmentsQuery,
    AccreditationsSortAttributes,
    ALL_ICON,
    FilterType,
    SegmentCustomFieldFragment
} from '../../generated/types';
import { CountriesService } from '../../services/countriesService';
import { DateTimeService } from '../../services/dateTimeService';
import { TranslationService } from '../../services/translationService';
import { isNonEmptyArray } from '../../util/array';
import { Filter, PossibleColumn, SegmentService } from '../segment';

export enum AccreditationDefaultColumns {
    Acronym = 'acronym',
    Address = 'address',
    AutoAccredit = 'autoAccredit',
    Category = 'category',
    Color = 'color',
    Conditions = 'conditions',
    CreatedAt = 'createdAt',
    CreatedBy = 'createdBy',
    Description = 'description',
    FillingRate = 'fillingRate',
    Icon = 'icon',
    IconColor = 'iconColor',
    Id = 'id',
    Latitude = 'latitude',
    Longitude = 'longitude',
    Name = 'name',
    PrivateNote = 'privateNote',
    Resources = 'resources',
    SlotDate = 'slotDate',
    SlotName = 'slotName',
    Tags = 'tags',
    WeezeventChecklistId = 'weezeventChecklistId',
    WeezeventIsSynchronized = 'weezeventIsSynchronized'
}

@injectable()
export class AccreditationsSegmentsService extends SegmentService {
    constructor(
        countriesService: CountriesService,
        dateTimeService: DateTimeService,
        translationService: TranslationService
    ) {
        super(countriesService, dateTimeService, translationService);
    }

    getAccreditationsFilters(
        eventInfos: AccreditationsSegmentsQuery['event'],
        customFields: SegmentCustomFieldFragment[]
    ): Filter[] {
        return compact([
            {
                slug: 'name',
                name: this.t('nom_de_l_accr_d_11908'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Text,
                hideEmpty: true
            },
            {
                slug: 'accreditation',
                name: this.t('Accreditation'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Select,
                hideEmpty: true,
                needLoading: true,
                eventId: eventInfos.id
            },
            {
                slug: AccreditationDefaultColumns.Description,
                name: this.t('Description'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Text
            },
            {
                slug: AccreditationDefaultColumns.PrivateNote,
                name: this.t('note_priv_e_06303'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Text
            },
            isNonEmptyArray(eventInfos.accreditationsCategories)
                ? {
                      slug: 'accreditationCategory',
                      name: this.t('cat_gorie_00291'),
                      category: this.t('accr_ditations_39450'),
                      fieldType: FilterType.Select,
                      values: eventInfos.accreditationsCategories,
                      hideEmpty: true
                  }
                : null,
            {
                slug: 'slotName',
                name: this.t('nom_du_cr_neau_54351'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Text
            },
            {
                slug: 'slotDate',
                name: this.t('Date'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Date
            },
            {
                slug: 'tag',
                name: 'Tag',
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Select,
                needLoading: true,
                organizationId: eventInfos.organizationId
            },
            {
                slug: 'accreditationCustomField',
                name: this.t('condition_61066'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Select,
                needLoading: true,
                organizationId: eventInfos.organizationId
            },
            {
                slug: 'acronym',
                name: this.t('Acronym'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Text
            },
            {
                slug: 'color',
                name: this.t('Color'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Text,
                hideEmpty: true
            },
            {
                slug: 'icon',
                name: this.t('ic_ne_55554'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Select,
                values: ALL_ICON.map((icon) => ({
                    id: icon,
                    name: this.t(icon)
                })),
                hideEmpty: true
            },
            {
                slug: 'address',
                name: this.t('Address'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Text
            },
            {
                slug: 'fillingRate',
                name: this.t('taux_de_remplis_11913'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Number,
                hideEmpty: true
            },
            {
                slug: AccreditationDefaultColumns.CreatedBy,
                name: this.t('cr_e_par_30622'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Select,
                hideEmpty: true,
                needLoading: true,
                organizationId: eventInfos.organizationId,
                eventId: eventInfos.id
            },
            {
                slug: AccreditationDefaultColumns.CreatedAt,
                name: this.t('cr_e_le_02658'),
                category: this.t('accr_ditations_39450'),
                fieldType: FilterType.Datetime,
                hideEmpty: true
            },
            ...this.customFieldsToFilters(customFields)
        ]);
    }

    getAccreditationsPossibleColumns(
        hasWeezeventV2: boolean,
        customFields: SegmentCustomFieldFragment[]
    ): PossibleColumn[] {
        const possibleColumns: PossibleColumn[] = [
            {
                slug: AccreditationDefaultColumns.Id,
                name: this.t('id_51738'),
                isSearchable: true,
                isSortable: true,
                sortAttribute: AccreditationsSortAttributes.Id,
                width: 100
            },
            {
                slug: AccreditationDefaultColumns.Name,
                name: this.t('nom_de_l_accr_d_11908'),
                isSearchable: true,
                isSortable: true,
                sortAttribute: AccreditationsSortAttributes.Name
            },
            {
                slug: AccreditationDefaultColumns.Category,
                name: this.t('nom_de_la_cat_g_03860'),
                isSearchable: true,
                isSortable: true,
                sortAttribute: AccreditationsSortAttributes.Category
            },
            {
                slug: AccreditationDefaultColumns.SlotName,
                name: this.t('nom_du_cr_neau_54351'),
                isSearchable: true
            },
            {
                slug: AccreditationDefaultColumns.SlotDate,
                name: this.t('Date')
            },
            {
                slug: AccreditationDefaultColumns.Resources,
                name: this.t('membres_11310'),
                width: 256
            },
            {
                slug: AccreditationDefaultColumns.Tags,
                name: this.t('tags_79499'),
                isSearchable: true
            },
            {
                slug: AccreditationDefaultColumns.Conditions,
                name: this.t('conditions_77756')
            },
            {
                slug: AccreditationDefaultColumns.Address,
                name: this.t('Address'),
                isSearchable: true
            },
            {
                slug: AccreditationDefaultColumns.Latitude,
                name: this.t('latitude_46176'),
                isSearchable: true
            },
            {
                slug: AccreditationDefaultColumns.Longitude,
                name: this.t('longitude_01695'),
                isSearchable: true
            },
            {
                slug: AccreditationDefaultColumns.Acronym,
                name: this.t('Acronym'),
                isSearchable: true,
                isSortable: true,
                sortAttribute: AccreditationsSortAttributes.Acronym
            },
            {
                slug: AccreditationDefaultColumns.Color,
                name: this.t('Color'),
                isSearchable: true,
                width: 80
            },
            {
                slug: AccreditationDefaultColumns.Icon,
                name: this.t('ic_ne_55554'),
                width: 80
            },
            {
                slug: AccreditationDefaultColumns.IconColor,
                name: this.t('ic_ne_et_couleu_54485'),
                width: 80
            },
            {
                slug: AccreditationDefaultColumns.FillingRate,
                name: this.t('taux_de_remplis_11913')
            },
            {
                slug: AccreditationDefaultColumns.CreatedBy,
                name: this.t('cr_e_par_30622'),
                isSearchable: true
            },
            {
                slug: AccreditationDefaultColumns.CreatedAt,
                name: this.t('cr_e_le_02658'),
                isSortable: true,
                sortAttribute: AccreditationsSortAttributes.CreatedAt
            },
            {
                slug: AccreditationDefaultColumns.AutoAccredit,
                name: this.t('AutoAccredit')
            }
        ];

        if (hasWeezeventV2) {
            possibleColumns.push(
                {
                    slug: AccreditationDefaultColumns.WeezeventIsSynchronized,
                    name: this.t('WeezeventIsSynchronized')
                },
                {
                    slug: AccreditationDefaultColumns.WeezeventChecklistId,
                    name: this.t('WeezeventChecklistId')
                }
            );
        }

        return possibleColumns.concat(this.customFieldsToPossibleColumns(customFields));
    }
}
